import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "../lib/api";
import { ESTADO_COLORS, ESTADO_LABELS } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  MessageSquare,
  Users,
  CheckSquare,
  TrendingUp,
  ArrowRight,
  Calendar,
  MapPin,
  ClipboardList,
  BellRing,
  Hourglass,
  Clock4,
  Timer,
} from "lucide-react";



const formatDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return value || "Sin fecha";
  try {
    const date = parseISO(value);
    if (isNaN(date.getTime())) return value || "Sin fecha";
    return format(date, "dd-MM-yyyy", { locale: es });
  } catch (error) {
    return value || "Sin fecha";
  }
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: "Total Consultas",
      value: Object.values(stats?.consultas || {}).reduce((a, b) => a + b, 0),
      icon: MessageSquare,
      color: "bg-primary",
      link: "/consultas",
    },
    {
      title: "Clientes",
      value: stats?.clientes_total || 0,
      icon: Users,
      color: "bg-secondary",
      link: "/clientes",
    },
    {
      title: "Tareas Pendientes",
      value: stats?.tareas_pendientes || 0,
      icon: CheckSquare,
      color: "bg-accent",
      textColor: "text-accent-foreground",
    },
    {
      title: "Conversión",
      value: `${Math.round(
        ((stats?.consultas?.convertida || 0) /
          (Object.values(stats?.consultas || {}).reduce((a, b) => a + b, 0) || 1)) *
          100
      )}%`,
      icon: TrendingUp,
      color: "bg-green-500",
    },
  ];

  const tareasPorCliente = stats?.tareas_por_cliente || [];
  const contactosPendientes = stats?.contactos_pendientes || [];
  const reservasTemporales = stats?.reservas_temporales || [];

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Resumen de tu negocio de fotomatones
        </p>
      </div>

      {/* KPI Cards - Bento Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {kpiCards.map((card, index) => (
          <Card
            key={card.title}
            className={`border-0 shadow-soft hover:shadow-lg transition-all duration-300 animate-fade-in stagger-${index + 1}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className={`font-heading text-3xl lg:text-4xl font-bold ${card.textColor || "text-primary"}`}>
                    {card.value}
                  </p>
                </div>
                <div className={`${card.color} p-3 rounded-xl`}>
                  <card.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
              </div>
              {card.link && (
                <Link
                  to={card.link}
                  className="inline-flex items-center gap-1 text-sm text-primary mt-4 hover:underline"
                >
                  Ver más <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Workload & Follow-ups */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Tareas por cliente */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Tareas pendientes por cliente
            </CardTitle>
            <Link to="/clientes">
              <Button variant="ghost" size="sm" className="gap-1">
                Ver clientes <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {tareasPorCliente.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                No hay tareas pendientes para clientes.
              </div>
            ) : (
              <div className="space-y-4">
                {tareasPorCliente.slice(0, 6).map((item) => (
                  <Link
                    key={item.cliente_id}
                    to={item.link || (item.entidad_tipo === "cliente" ? `/clientes/${item.cliente_id}` : `/consultas/${item.cliente_id}`)}
                    className="block p-4 rounded-2xl border border-border/50 hover:border-primary/50 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-primary">{item.cliente_nombre}</p>
                          <Badge variant="outline" className="text-[11px]">
                            {item.entidad_tipo === "cliente" ? "Cliente" : "Consulta"}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {(item.tipo_evento || "Sin tipo") + " • " + (formatDate(item.fecha_evento) || "Sin fecha")}
                        </p>
                      </div>
                      <Badge variant="secondary" className="rounded-full">
                        {item.tareas.length} tarea{item.tareas.length !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                    <ul className="text-sm text-muted-foreground mt-3 space-y-1 list-disc list-inside">
                      {item.tareas.slice(0, 3).map((task) => (
                        <li key={task.id}>{task.titulo}</li>
                      ))}
                      {item.tareas.length > 3 && (
                        <li className="text-xs text-muted-foreground/80">
                          +{item.tareas.length - 3} más
                        </li>
                      )}
                    </ul>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contactos pendientes */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <BellRing className="w-5 h-5 text-primary" />
              Contactos vencidos
            </CardTitle>
            <Link to="/consultas">
              <Button variant="ghost" size="sm" className="gap-1">
                Ver consultas <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {contactosPendientes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BellRing className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No hay seguimientos atrasados.
              </div>
            ) : (
              <div className="space-y-3">
                {contactosPendientes.slice(0, 8).map((consulta) => (
                  <Link
                    key={consulta.id}
                    to={`/consultas/${consulta.id}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-muted/30 hover:bg-muted/50 transition"
                  >
                    <div>
                      <p className="font-medium text-primary">{consulta.nombre}</p>
                      <p className="text-xs text-muted-foreground">
                        Próximo contacto: {consulta.fecha_proximo_contacto ? formatDate(consulta.fecha_proximo_contacto) : "Sin fecha"}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive" className="gap-1">
                        <Clock4 className="w-3 h-3" />
                        {consulta.dias_atraso === 0 ? "Hoy" : `${consulta.dias_atraso}d atraso`}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reservas temporales */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Hourglass className="w-5 h-5 text-primary" />
              Reservas temporales (72h)
            </CardTitle>
            <Link to="/consultas">
              <Button variant="ghost" size="sm" className="gap-1">
                Gestionar <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {reservasTemporales.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Hourglass className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No hay reservas temporales activas.
              </div>
            ) : (
              <div className="space-y-3">
                {reservasTemporales.slice(0, 6).map((consulta) => (
                  <Link
                    key={consulta.id}
                    to={`/consultas/${consulta.id}`}
                    className={`flex items-center justify-between gap-3 p-3 rounded-2xl border ${
                      consulta.expirado ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50/60"
                    }`}
                  >
                    <div>
                      <p className="font-semibold text-primary">{consulta.nombre}</p>
                      <p className="text-xs text-muted-foreground">
                        Evento: {formatDate(consulta.fecha_evento) || "Sin fecha"}
                      </p>
                    </div>
                    <Badge variant={consulta.expirado ? "destructive" : "outline"} className="gap-1">
                      <Timer className="w-3 h-3" />
                      {consulta.expirado ? "Expirada" : `${consulta.horas_restantes}h`}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Consultas */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl">Consultas Recientes</CardTitle>
          <Link to="/consultas">
            <Button variant="ghost" size="sm" className="gap-1">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.consultas_recientes?.length > 0 ? (
            <div className="space-y-4">
              {stats.consultas_recientes.map((consulta) => (
                <Link
                  key={consulta.id}
                  to={`/consultas/${consulta.id}`}
                  className="block p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-primary truncate group-hover:text-secondary transition-colors">
                          {consulta.nombre}
                        </h3>
                        <Badge className={ESTADO_COLORS[consulta.estado]} variant="outline">
                          {ESTADO_LABELS[consulta.estado]}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" strokeWidth={1.5} />
                          {formatDate(consulta.fecha_evento)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" strokeWidth={1.5} />
                          {consulta.ubicacion}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay consultas recientes</p>
              <Link to="/consultas">
                <Button variant="outline" className="mt-4">
                  Crear primera consulta
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
